import { useRetracked } from 'js/lib/retracked';

import { useTracker } from '@coursera/event-pulse/react';

import {
  AUTOCOMPLETE_PREFIX_DELIMITER,
  AUTOCOMPLETE_SECTION_PREFIXES,
  autocompletePrefixes,
} from 'bundles/search-common/constants/autocomplete';
import type { AutocompleteMode } from 'bundles/search-common/constants/autocomplete';
import { getAutocompleteEventingData, isExactPartnerNameMatch } from 'bundles/search-common/utils/autocomplete';
import type { AutocompleteItem, AutocompleteItems } from 'bundles/search-common/utils/autocomplete';
import { extractFiltersForEventing } from 'bundles/search-common/utils/providerEventingUtils';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';

const useTrackSearch = () => {
  const track = useRetracked();
  const tracker = useTracker();
  const isConsumerSearchPage = useIsConsumerSerp();
  const isEqpPage = useIsEqp();
  const isFiltersRedesignSurface = isEqpPage || isConsumerSearchPage;

  return (
    rawQuery: string,
    autocompleteMode: AutocompleteMode,
    recentlyViewedItems: AutocompleteItems,
    options?: {
      suggestionIndex?: number;
      selectedItem?: AutocompleteItem;
      skillId?: string;
      skillName?: string;
    }
  ) => {
    const { suggestionIndex, selectedItem, skillId, skillName } = options || {};
    const sectionPrefix = autocompletePrefixes.find((prefix) => rawQuery.startsWith(prefix));
    let displayedQuery = sectionPrefix ? rawQuery.replace(sectionPrefix, '') : rawQuery;
    const filterData = isFiltersRedesignSurface
      ? { filter: extractFiltersForEventing(window.location.search || '') }
      : {};

    if (sectionPrefix) {
      displayedQuery = rawQuery.replace(sectionPrefix, '');

      track({
        action: 'click',
        trackingName: 'autocomplete_item',
        trackingData: { name: displayedQuery, autocompleteSection: sectionPrefix },
      });
      const trackingData = getAutocompleteEventingData(rawQuery, autocompleteMode, recentlyViewedItems, {
        suggestionIndex,
        skillId,
        skillName,
      });

      const isAiPromptSection =
        sectionPrefix === AUTOCOMPLETE_SECTION_PREFIXES.AI_PROMPTS_ZERO_STATE ||
        sectionPrefix === AUTOCOMPLETE_SECTION_PREFIXES.AI_PROMPTS_AS_YOU_TYPE ||
        (sectionPrefix === AUTOCOMPLETE_SECTION_PREFIXES.RECENT_SEARCHES && selectedItem?.isAiPrompt);

      if (isAiPromptSection && selectedItem) {
        const promptId = selectedItem.id.includes(AUTOCOMPLETE_PREFIX_DELIMITER)
          ? (selectedItem.id.split(AUTOCOMPLETE_PREFIX_DELIMITER).pop() ?? selectedItem.id)
          : selectedItem.id;
        trackingData.queryPrompt = {
          id: promptId,
          location: 'autocomplete',
          promptText: selectedItem.name,
          intentType: selectedItem.intentType,
          routeType: selectedItem.routeType,
          routeParameter: selectedItem.routeParameter,
        };
      }
      tracker('click_autocomplete_suggestions', trackingData);
      tracker('perform_search', {
        searchedQuery: displayedQuery,
        sourceLinkCategory: 'autocomplete_suggestions',
        ...filterData,
      });
    } else if (isExactPartnerNameMatch(rawQuery)) {
      track({
        action: 'click',
        trackingName: 'autocomplete_item',
        trackingData: { name: displayedQuery, autocompleteSection: 'Partner' },
      });
      const trackingData = getAutocompleteEventingData(rawQuery, autocompleteMode, recentlyViewedItems);
      tracker('click_autocomplete_suggestions', trackingData);
      tracker('perform_search', {
        searchedQuery: displayedQuery,
        sourceLinkCategory: 'autocomplete_suggestions',
        ...filterData,
      });
    } else {
      const trackingData = { searchText: rawQuery, page: window.location.pathname };
      track({ action: 'search', trackingName: 'algolia_search_page_query_updated', trackingData });
      tracker('perform_search', { searchedQuery: rawQuery, ...filterData });
    }
  };
};
export default useTrackSearch;
